import { useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { Link, useT, useLang, stripLang, localizePath } from "@/i18n";
import logo from "@/assets/logo.webp";

const LanguageSwitcher = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { pathname } = useLocation();
  const lang = useLang();
  const frPath = stripLang(pathname);
  const enPath = localizePath(frPath, "en");
  const base =
    "px-2 py-1 text-sm font-semibold rounded-md transition-colors";
  return (
    <div className="flex items-center gap-1 border border-border rounded-lg p-0.5" aria-label="Language / Langue">
      <a
        href={frPath}
        hrefLang="fr-CA"
        onClick={onNavigate}
        aria-current={lang === "fr" ? "true" : undefined}
        className={`${base} ${lang === "fr" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}
      >
        FR
      </a>
      <a
        href={enPath}
        hrefLang="en-CA"
        onClick={onNavigate}
        aria-current={lang === "en" ? "true" : undefined}
        className={`${base} ${lang === "en" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}
      >
        EN
      </a>
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVillesOpen, setIsVillesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const lang = useLang();
  const home = localizePath("/", lang);

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    const id = hash.replace("#", "");
    if (stripLang(location.pathname) !== "/") {
      e.preventDefault();
      navigate(home, { state: { scrollTo: id } });
      setIsMenuOpen(false);
      return;
    }
    // On home: smooth scroll
    const el = document.getElementById(id);
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMenuOpen(false);
    }
  };

  const navLinks = [{
    href: "#accueil",
    label: t("Accueil", "Home")
  }, {
    href: "#services",
    label: t("Services", "Services")
  }, {
    href: "#avant-apres",
    label: t("Avant/Après", "Before/After")
  }, {
    href: "/blog",
    label: t("Blog", "Blog")
  }, {
    href: "#soumission",
    label: t("Soumission", "Get a quote")
  }, {
    href: "#contact",
    label: t("Contact", "Contact")
  }];

  const villesLinks = [
    { href: "/nettoyage-bacs-valleyfield", label: "Valleyfield" },
    { href: "/nettoyage-bacs-beauharnois", label: "Beauharnois" },
    { href: "/nettoyage-bacs-haut-saint-laurent", label: "Haut-Saint-Laurent" },
    { href: "/nettoyage-bacs-soulanges", label: "Soulanges" },
  ];
  return <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container-narrow mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="Logo Nettoyage Boudreau - Service de nettoyage de bacs à Valleyfield, Beauharnois, Soulanges et Haut-Saint-Laurent" 
              className="h-14 w-auto"
              width="56"
              height="56"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 ml-12">
            {navLinks.map(link => link.href.startsWith("/") ? (
              <Link key={link.href} to={link.href} className="text-foreground/80 hover:text-primary font-medium transition-colors duration-200">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className="text-foreground/80 hover:text-primary font-medium transition-colors duration-200">
                {link.label}
              </a>
            ))}
            {/* Villes dropdown */}
            <div className="relative group">
              <button
                type="button"
                className="flex items-center gap-1 text-foreground/80 hover:text-primary font-medium transition-colors duration-200"
                aria-haspopup="true"
                aria-expanded="false"
              >
                {t("Villes", "Cities")}
                <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block z-50">
                <div className="bg-background border border-border rounded-xl shadow-lg py-2 min-w-[260px]">
                  {villesLinks.map((v) => (
                    <Link
                      key={v.href}
                      to={v.href}
                      className="block px-4 py-2 text-foreground/80 hover:text-primary hover:bg-muted font-medium transition-colors"
                    >
                      {t("Nettoyage de bacs", "Bin cleaning")} — {v.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <Button variant="outline" size="default" asChild>
              <a href="tel:+14508029003">
                <Phone className="w-4 h-4" />
                {t("Appeler", "Call")}
              </a>
            </Button>
            <Button variant="cta" size="default" asChild>
              <a href="#soumission" onClick={(e) => handleHashClick(e, "#soumission")}>{t("Obtenir une soumission", "Get a free quote")}</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-foreground" aria-label={t("Ouvrir le menu", "Toggle menu")}>
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && <div className="lg:hidden py-4 border-t border-border animate-fade-in">
            <nav className="flex flex-col gap-4">
              {navLinks.map(link => link.href.startsWith("/") ? (
                <Link key={link.href} to={link.href} onClick={() => setIsMenuOpen(false)} className="text-foreground/80 hover:text-primary font-medium transition-colors duration-200 py-2">
                  {link.label}
                </Link>
              ) : (
                <a key={link.href} href={link.href} onClick={(e) => handleHashClick(e, link.href)} className="text-foreground/80 hover:text-primary font-medium transition-colors duration-200 py-2">
                  {link.label}
                </a>
              ))}
              {/* Villes mobile */}
              <div className="border-t border-border pt-2">
                <button
                  type="button"
                  onClick={() => setIsVillesOpen(!isVillesOpen)}
                  className="flex items-center justify-between w-full text-foreground/80 hover:text-primary font-medium transition-colors duration-200 py-2"
                  aria-expanded={isVillesOpen}
                >
                  {t("Villes desservies", "Cities we serve")}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isVillesOpen ? "rotate-180" : ""}`} />
                </button>
                {isVillesOpen && (
                  <div className="flex flex-col gap-1 pl-3 mt-1">
                    {villesLinks.map((v) => (
                      <Link
                        key={v.href}
                        to={v.href}
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsVillesOpen(false);
                        }}
                        className="text-foreground/70 hover:text-primary transition-colors py-2"
                      >
                        {t("Nettoyage de bacs", "Bin cleaning")} — {v.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-3 pt-4 border-t border-border">
                <Button variant="outline" asChild className="w-full">
                  <a href="tel:+14508029003">
                    <Phone className="w-4 h-4" />
                    {t("Appeler maintenant", "Call now")}
                  </a>
                </Button>
                <Button variant="cta" asChild className="w-full">
                  <a href="#soumission" onClick={(e) => handleHashClick(e, "#soumission")}>{t("Obtenir une soumission", "Get a free quote")}</a>
                </Button>
              </div>
            </nav>
          </div>}
      </div>
    </header>;
};
export default Header;